/* ==========================================================================
   Guided tours — short, page-specific walkthroughs.
   Targets point at elements the pages already render (classes, titles,
   aria-labels or visible text), so the shared header/sidebar code is not
   touched. A step whose target is missing (e.g. no alert rows today, or a
   module the user can't see) is skipped automatically.
   ========================================================================== */

/** CSS selector, or visible text (optionally lifted to the closest matching ancestor). */
export type TourTarget = string | { text: string; closest?: string; within?: string };

export interface TourStep {
    title: string;
    body: string;
    /** Omit for a centred intro card. */
    target?: TourTarget;
    placement?: 'top' | 'bottom' | 'left' | 'right';
}

export type TourId =
    | 'hub' | 'entry' | 'upload' | 'alerts' | 'inventory'
    | 'siteinfo' | 'om' | 'ptw' | 'simulation' | 'dsm' | 'vendors' | 'documentation'
    | 'compliance' | 'histogram' | 'profile' | 'selectSite' | 'selectCompany'
    | 'adminDashboard' | 'adminUsers' | 'adminSites' | 'adminCompanies' | 'adminTokens'
    | 'adminSecurity' | 'adminActivity' | 'adminAttendance' | 'adminCleanup' | 'adminDeletion';

const adminStats = (body: string): TourStep => ({
    title: 'Numbers first', body, target: '.admin-stat-card', placement: 'bottom',
});
const adminSearch: TourStep = {
    title: 'Narrow it down',
    body: 'Search and filters update the list instantly.',
    target: '.admin-search-wrap',
    placement: 'bottom',
};
const adminRowActions: TourStep = {
    title: 'Act on a row',
    body: 'The icons on each row open, edit or manage that record. Hover one to see what it does.',
    target: '.admin-row .admin-icon-button',
    placement: 'left',
};

export const TOURS: Record<TourId, { name: string; steps: TourStep[] }> = {
    hub: {
        name: 'Getting around',
        steps: [
            {
                title: 'Welcome to AxonMetric',
                body: 'A 30-second look at where everything lives. Use ← → or the buttons.',
            },
            {
                title: 'Every module, one rail',
                body: 'Hover this rail to open it. It’s on every page, so you’re never more than a click away.',
                target: '.main-sidebar-container',
                placement: 'right',
            },
            {
                title: 'Jump anywhere',
                body: 'The 9 dots open any module, or a single sheet like Weather or Breakdowns.',
                target: 'header button[title="App Launcher"]',
                placement: 'bottom',
            },
            {
                title: 'Live performance',
                body: 'Today’s power, energy, PR and weather. Click the card to dive in.',
                target: '.np-row--feature > :first-child',
                placement: 'bottom',
            },
            {
                title: 'Daily reports',
                body: 'Enter or correct the day’s generation report here.',
                target: '.np-row--feature > :nth-child(2)',
                placement: 'bottom',
            },
            {
                title: 'Your toolbox',
                body: 'O&M, alerts, uploads, spares and more. A lock means your admin hasn’t enabled it yet.',
                target: '.np-row--modules',
                placement: 'top',
            },
            {
                title: 'Your account',
                body: 'Profile, password, switch plant and logout. “Site Navigation” brings you back here.',
                target: 'header.header-glass .glass-button',
                placement: 'bottom',
            },
            {
                title: 'Replay anytime',
                body: 'Every page has its own quick tour. Tap here whenever you need it.',
                target: '.ax-tour-fab',
                placement: 'top',
            },
        ],
    },
    entry: {
        name: 'Daily data entry',
        steps: [
            {
                title: 'Pick a sheet',
                body: 'Daily Report, Weather, Inverters, Breakdowns and Plant Meters, all in one place.',
                target: '.entry-sidebar-container',
                placement: 'right',
            },
            {
                title: 'Add a day',
                body: 'New Date adds a row for the date you choose.',
                target: 'button[title="Add a new row"]',
                placement: 'bottom',
            },
            {
                title: 'Type, then Save',
                body: 'Fill the grid cells, then Save. Wait for the confirmation before leaving.',
                target: 'button[title="Save data"]',
                placement: 'bottom',
            },
            {
                title: 'Take it to Excel',
                body: 'Export downloads this sheet as an Excel file.',
                target: 'button[title="Export data to Excel file"]',
                placement: 'bottom',
            },
        ],
    },
    upload: {
        name: 'Uploading a sheet',
        steps: [
            {
                title: 'Drop your file',
                body: 'Drag an .xlsx, .xls or .csv here (up to 10 MB), or click Choose File.',
                target: { text: 'Drag & Drop File', closest: '.border-dashed' },
                placement: 'right',
            },
            {
                title: 'Tell us which sheet',
                body: 'Pick the matching sheet so the columns land in the right place.',
                target: { text: 'Select File Name', closest: 'div' },
                placement: 'right',
            },
            {
                title: 'Send it',
                body: 'Upload to Server runs two steps: upload, then database processing. Stay until both finish.',
                target: { text: 'Upload to Server', closest: 'button' },
                placement: 'top',
            },
            {
                title: 'Need a template?',
                body: 'Download any sheet as Excel. Same columns, ready to fill.',
                target: { text: 'Download File', closest: '.rounded-xl' },
                placement: 'left',
            },
        ],
    },
    alerts: {
        name: 'Working with alerts',
        steps: [
            {
                title: 'Choose the day',
                body: 'Step through days with the arrows, or click the date to pick one.',
                target: 'button[aria-label="Click to select date"]',
                placement: 'bottom',
            },
            {
                title: 'No alerts yet?',
                body: 'Run Pipeline generates alerts for the selected day.',
                target: '.btn-pipeline',
                placement: 'bottom',
            },
            {
                title: 'Close the loop',
                body: 'The pencil opens feedback: accuracy, status, energy loss and photos. Save needs a Fault Description.',
                target: 'button[title="Edit alert"]',
                placement: 'left',
            },
            {
                title: 'Stay current',
                body: 'Refresh reloads the day’s alerts.',
                target: 'button[aria-label="Refresh data"]',
                placement: 'bottom',
            },
        ],
    },
    inventory: {
        name: 'Managing spares',
        steps: [
            {
                title: 'Find any spare',
                body: 'Search across every column: name, make, serial number and more.',
                target: 'input[placeholder="Search all fields..."]',
                placement: 'bottom',
            },
            {
                title: 'Stock at a glance',
                body: 'Installed quantity, spares available and consumption, updated as you edit.',
                target: '.inventory-page .np-kpi',
                placement: 'bottom',
            },
            {
                title: 'Add one…',
                body: 'Add Item opens the spare form.',
                target: { text: 'Add Item', closest: 'button' },
                placement: 'bottom',
            },
            {
                title: '…or many',
                body: 'Bulk Import loads a whole sheet of spares at once.',
                target: { text: 'Bulk Import', closest: 'button' },
                placement: 'bottom',
            },
        ],
    },
    siteinfo: {
        name: 'Plant details',
        steps: [
            { title: 'Where the plant is', body: 'Zoom and drag the map; the location details sit underneath.', target: '[data-tour="si-map"]', placement: 'right' },
            { title: 'Plant at a glance', body: 'Identity, capacity, commercial terms, company and site manager.', target: '[data-tour="si-overview"]', placement: 'left' },
            { title: 'More specs', body: 'The arrows switch to the full technical specification table.', target: '[data-tour="si-pager"]', placement: 'bottom' },
        ],
    },
    om: {
        name: 'Maintenance planning',
        steps: [
            { title: 'O&M menu', body: 'PM Calendar, PTW and the field checklists all live in this panel.', target: '.om-sidebar', placement: 'right' },
            { title: 'Plan the work', body: 'New Task schedules a job: activity, time, who does it and on which equipment.', target: { text: 'New Task', closest: 'button' }, placement: 'bottom' },
            { title: 'Change the view', body: 'Switch between month, week and day.', target: '.omx-seg[aria-label="Calendar view"]', placement: 'bottom' },
            { title: 'Track progress', body: 'Completed, scheduled and overdue tasks, plus the completion rate.', target: '.omx-kpis', placement: 'bottom' },
        ],
    },
    ptw: {
        name: 'Permits to work',
        steps: [
            { title: 'Raise a permit', body: 'New PTW opens a 5-step wizard: work order, classification, hazards, roles, review.', target: { text: 'New PTW', closest: 'button' }, placement: 'bottom' },
            { title: 'Find your permits', body: 'Mine shows permits waiting on you; Pending and Active show where the rest stand.', target: '.omx-seg[aria-label="Permit filter"]', placement: 'bottom' },
            { title: 'How it’s going', body: 'Issuance time and permit counts by status.', target: { text: 'Automation KPIs', closest: '.omx-card' }, placement: 'bottom' },
            { title: 'Cards or list', body: 'Pick the layout that suits you, then open any permit to act on it.', target: '[aria-label="Permit layout"]', placement: 'left' },
        ],
    },
    simulation: {
        name: 'Simulation tools',
        steps: [
            { title: 'Four tools', body: 'Token wallet, raw-data download, alert generation and RTC optimisation.', target: '[data-tour="sim-nav"]', placement: 'right' },
            { title: 'Your balance', body: 'Each run uses tokens. Check what’s available before you start.', target: { text: 'Available Tokens', closest: '.rounded-lg, .rounded-xl' }, placement: 'bottom' },
            { title: 'Running low?', body: 'Request a recharge here; your admin approves it.', target: { text: 'Request Token Recharge', closest: '.rounded-lg' }, placement: 'top' },
        ],
    },
    dsm: {
        name: 'DSM preview',
        steps: [
            { title: 'Coming soon', body: 'DSM is still being built, so nothing here is live data yet.', target: '.np-note', placement: 'bottom' },
            { title: 'What it will do', body: 'Forecasting, grid feed, scheduling and automation.', target: '.np-theme .grid', placement: 'top' },
        ],
    },
    vendors: {
        name: 'Vendors and invoices',
        steps: [
            { title: 'Spend at a glance', body: 'Vendors, active contracts, renewals, PO value and invoiced value.', target: '.vendor-page .np-kpi', placement: 'bottom' },
            { title: 'Find a vendor', body: 'Search by name, PAN, GST or contact, then filter by invoice status.', target: 'input[placeholder="Search vendors..."]', placement: 'bottom' },
            { title: 'Add a vendor', body: 'Add New Vendor opens the form for KYC, bank and PO details.', target: { text: 'Add New Vendor', closest: 'button' }, placement: 'left' },
            { title: 'Bulk and export', body: 'Bulk Upload loads many vendors or invoices at once; Export downloads them.', target: { text: 'Bulk Upload', closest: 'button' }, placement: 'bottom' },
        ],
    },
    documentation: {
        name: 'Documents',
        steps: [
            { title: 'Choose a category', body: 'Manuals, SLDs, knowledge base, contracts and warranties.', target: '.np-theme h2 + .grid', placement: 'top' },
            { title: 'Add files', body: 'Drag files here or click “choose files”. View and download from the list below.', target: '.np-theme .border-dashed', placement: 'bottom' },
            { title: 'Good to know', body: 'Files aren’t saved to the server yet; they stay in this tab.', target: '.np-note', placement: 'bottom' },
        ],
    },
    compliance: {
        name: 'Compliance preview',
        steps: [
            { title: 'Sample data', body: 'These requirements are examples until the tracker goes live.', target: '.np-note', placement: 'bottom' },
            { title: 'Your score', body: 'Overall compliance plus compliant, pending and overdue counts.', target: '.np-kpi', placement: 'bottom' },
            { title: 'Find a requirement', body: 'Search by requirement, category or owner.', target: 'input[aria-label="Search compliance requirements"]', placement: 'bottom' },
        ],
    },
    histogram: {
        name: 'Energy export chart',
        steps: [
            { title: 'Daily export', body: 'Energy exported each day for the selected period.', target: '.recharts-wrapper', placement: 'top' },
            { title: 'Change the period', body: 'Pick a preset, from the last 7 days to all time.', target: '.dashboard-chart-page select', placement: 'bottom' },
        ],
    },
    profile: {
        name: 'Your profile',
        steps: [
            { title: 'Three tabs', body: 'Profile details, password and settings.', target: '[data-tour="profile-tabs"]', placement: 'bottom' },
            { title: 'Edit your details', body: 'Edit Profile unlocks the fields and lets you change your photo.', target: { text: 'Edit Profile', closest: 'button' }, placement: 'bottom' },
            { title: 'New password', body: 'Open Password, enter the current one and the new one twice.', target: { text: 'Password', closest: 'button', within: '[data-tour="profile-tabs"]' }, placement: 'bottom' },
        ],
    },
    selectSite: {
        name: 'Choosing a plant',
        steps: [
            { title: 'Find a plant', body: 'Search by name or code.', target: '.axm-search', placement: 'bottom' },
            { title: 'Two views', body: 'Sites lists every plant; Portfolio sums them up.', target: '[data-tour="ss-views"]', placement: 'bottom' },
            { title: 'Open a plant', body: 'Click a row to enter that plant’s dashboard.', target: '[data-tour="ss-table"]', placement: 'right' },
            { title: 'On the map', body: 'Every plant’s location at a glance.', target: '[data-tour="ss-map"]', placement: 'left' },
            { title: 'Need help later?', body: 'Guide explains every module and what to click.', target: { text: 'Guide', closest: 'button' }, placement: 'bottom' },
        ],
    },
    selectCompany: {
        name: 'Choosing a company',
        steps: [
            { title: 'Find a company', body: 'Search onboarded companies by name or code.', target: '.axc-search', placement: 'bottom' },
            { title: 'Open a company', body: 'Click a card to see that company’s plants.', target: '.axc-grid .axc-card', placement: 'right' },
            { title: 'Admin and guide', body: 'Admin manages users, sites and tokens; Guide explains the app.', target: { text: 'Guide', closest: 'button' }, placement: 'bottom' },
        ],
    },
    adminDashboard: {
        name: 'Admin overview',
        steps: [
            { title: 'Admin menu', body: 'Customers, tokens, sites, users, security and activity are all here.', target: '[data-tour="admin-nav"]', placement: 'right' },
            adminStats('Users, sites, customers and security alerts in one row.'),
            { title: 'Stay current', body: 'Refresh overview reloads every number.', target: 'button[aria-label="Refresh overview"]', placement: 'bottom' },
            { title: 'Back to the plant', body: 'Back to Site returns you to the operations app.', target: { text: 'Back to Site', closest: 'button, a' }, placement: 'right' },
        ],
    },
    adminUsers: {
        name: 'Managing users',
        steps: [
            { title: 'Add people', body: 'Create user sets role, site and module access in one form.', target: '.admin-toolbar .admin-button-primary', placement: 'bottom' },
            { title: 'Many at once', body: 'Upload Data creates users from a sheet.', target: { text: 'Upload Data', closest: 'button' }, placement: 'bottom' },
            adminSearch,
            adminRowActions,
        ],
    },
    adminSites: {
        name: 'Managing sites',
        steps: [
            { title: 'Add a plant', body: 'Create site captures capacity, location, tariff and its telemetry database.', target: '.admin-toolbar .admin-button-primary', placement: 'bottom' },
            { title: 'Telemetry links', body: 'Database Mappings tests and connects each plant’s data source.', target: { text: 'Database Mappings', closest: 'button' }, placement: 'bottom' },
            adminSearch,
            adminRowActions,
        ],
    },
    adminCompanies: {
        name: 'Managing customers',
        steps: [
            { title: 'Onboard a customer', body: 'Create customer sets plan, limits, modules and tokens.', target: '.admin-toolbar .admin-button-primary', placement: 'bottom' },
            adminStats('Customers by status and plan.'),
            adminSearch,
            adminRowActions,
        ],
    },
    adminTokens: {
        name: 'Simulation tokens',
        steps: [
            { title: 'Give tokens', body: 'Batch Assign tops up several users at once.', target: '.admin-toolbar .admin-button-primary', placement: 'bottom' },
            { title: 'Find a company', body: 'Search by name or code to see issued, mapped and remaining tokens.', target: 'input[placeholder="Search company name or code"]', placement: 'bottom' },
        ],
    },
    adminSecurity: {
        name: 'Security monitoring',
        steps: [
            adminStats('Login attempts, failures, locked accounts and suspicious IPs.'),
            { title: 'Refresh', body: 'Pull the latest attempts, or leave live mode on.', target: 'button[aria-label="Refresh security data"]', placement: 'bottom' },
            adminSearch,
        ],
    },
    adminActivity: {
        name: 'User activity',
        steps: [
            adminStats('Who did what, and how much, at a glance.'),
            { title: 'Filter the stream', body: 'Narrow by user, username or activity type.', target: '.admin-filter-grid', placement: 'bottom' },
        ],
    },
    adminAttendance: {
        name: 'Site attendance',
        steps: [
            adminStats('Check-ins and geotagged proofs across sites.'),
            adminSearch,
        ],
    },
    adminCleanup: {
        name: 'Data cleanup',
        steps: [
            { title: 'Careful here', body: 'Choose the scope and modules, then back up before any permanent delete.', target: '.admin-toolbar', placement: 'bottom' },
            { title: 'Find the user', body: 'Search by username or email.', target: 'input[placeholder="Search username/email"]', placement: 'bottom' },
        ],
    },
    adminDeletion: {
        name: 'Deletion requests',
        steps: [
            { title: 'Find a request', body: 'Search by ticket ID, email, name or company.', target: 'input[placeholder^="Search by ticket ID"]', placement: 'bottom' },
        ],
    },
};
